import React, { useState, useEffect } from 'react';
import {
  Box,
  Tabs,
  Tab,
  Typography,
  Alert,
  useTheme
} from '@mui/material';
import SyncIcon from '@mui/icons-material/Sync';
import SettingsIcon from '@mui/icons-material/Settings';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import HistoryIcon from '@mui/icons-material/History';

import Breadcrumb from 'components/Breadcrumb';
import ConfigTab from './components/ConfigTab';
import SyncTab from './components/SyncTab';
import HistoryTab from './components/HistoryTab';
import { regionSyncService } from './services/RegionSyncService';
import { logger } from 'utils/logger';

const log = logger.getLogger('RegionSyncPage');

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`region-sync-tabpanel-${index}`}
      aria-labelledby={`region-sync-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `region-sync-tab-${index}`,
    'aria-controls': `region-sync-tabpanel-${index}`,
  };
}

const RegionSyncPage: React.FC = () => {
  const theme = useTheme();
  const [activeTab, setActiveTab] = useState(0);
  const [serviceStatus, setServiceStatus] = useState<'checking' | 'online' | 'offline'>('checking');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    checkServiceHealth();
  }, []);

  const checkServiceHealth = async () => {
    try {
      setServiceStatus('checking');
      await regionSyncService.healthCheck();
      setServiceStatus('online');
      setError(null);
    } catch (err) {
      log.error('Service health check failed:', err);
      setServiceStatus('offline');
      setError('Region Sync service is currently unavailable. Some features may not work.');
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  return (
    <Box>
      <Breadcrumb
        items={[
          { label: 'Region Sync', icon: <SyncIcon /> }
        ]}
      />

      {error && (
        <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Box
        sx={{
          borderBottom: 1,
          borderColor: 'divider',
          backgroundColor: theme.palette.background.default,
          borderRadius: '8px 8px 0 0',
        }}
      >
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          aria-label="Region Sync tabs"
          sx={{
            '& .MuiTab-root': {
              textTransform: 'none',
              fontWeight: 500,
              minHeight: 56,
            },
          }}
        >
          <Tab
            icon={<HistoryIcon />}
            iconPosition="start"
            label="History"
            {...a11yProps(0)}
          />
          <Tab
            icon={<PlayArrowIcon />}
            iconPosition="start"
            label="Sync"
            {...a11yProps(1)}
            disabled={serviceStatus === 'offline'}
          />
          <Tab
            icon={<SettingsIcon />}
            iconPosition="start"
            label="Configurations"
            {...a11yProps(2)}
          />
        </Tabs>
      </Box>

      <Box sx={{ pt: 1 }}>
        <TabPanel value={activeTab} index={0}>
          <HistoryTab />
        </TabPanel>
        <TabPanel value={activeTab} index={1}>
          <SyncTab onSyncStarted={() => setActiveTab(0)} />
        </TabPanel>
        <TabPanel value={activeTab} index={2}>
          <ConfigTab />
        </TabPanel>
      </Box>

      {/* Service status indicator */}
      <Box
        sx={{
          position: 'fixed',
          bottom: 16,
          right: 16,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          backgroundColor: theme.palette.background.paper,
          padding: '8px 16px',
          borderRadius: 2,
          boxShadow: theme.shadows[2],
        }}
      >
        <Box
          sx={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            backgroundColor:
              serviceStatus === 'online'
                ? theme.palette.success.main
                : serviceStatus === 'offline'
                ? theme.palette.error.main
                : theme.palette.warning.main,
          }}
        />
        <Typography variant="caption" color="text.secondary">
          {serviceStatus === 'online'
            ? 'Service Online'
            : serviceStatus === 'offline'
            ? 'Service Offline'
            : 'Checking...'}
        </Typography>
      </Box>
    </Box>
  );
};

export default RegionSyncPage;
